import { Button, ButtonVariants, CustomClaimValue, Device, DeviceCommand, DeviceCommands, Devices, DeviceStatus, FIRMWARE_PACKAGE_NAME, Icons, IRebootCommand, LastTargetEntity, Organizations, RebootCommand, useAuth, UserError, VersionReport, Versions } from "@app/shared";
import { format } from "date-fns-tz";
import { MouseEventHandler, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { EditDeviceCommand, getStatusHoverText, SearchInput } from ".";
import { BatchDeviceUpgradeView } from "./BatchDeviceUpgradeView";
import { compareVersions, toastError, toastSuccess } from "../../helpers";
import { hasClaim, useConfirmDialog, useDialog } from "../../hooks";
import { useAlertsUiVisible } from "../../hooks/useAlertsUiVisible";
import { useChangeLocationDialog } from "../../hooks/useDeviceLocation";
import { useEditModelDialog } from "../../hooks/useEditModelDialog";
import { Pages } from "../pages";
import { DeviceTab } from "../pages/facility/deviceTabs";
import { FacilityTab } from "../pages/facility/facilityTabs";
import { DeviceProvisionDialog } from "./DeviceProvisionDialog";
import { DropDownMenu } from "./DropDownMenu";
import { DeviceCommandTypes } from "./EditDeviceCommand/DeviceCommandEditor";
import { EditDeviceDetails } from "./EditDeviceDetails";
import { HoverTooltip } from "./HoverTooltip";
import { IconLink } from "./IconLink";
import { Spinner } from "./loading";
import { CardPosition, SortableTable, SortableTableHeaderType } from "./SortableTable";
import { StatusDot } from "./StatusDot";

type DevicesTableProps = {
    devices: Device[] | undefined;
    lastTargets?: Array<LastTargetEntity>;
    isLoading?: boolean;
    facilityId?: string;
    onChange?: () => void;
};
type TableDataType = Device & { lastUpdate: Date | undefined };

const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];
const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_SORT_KEY = "name";

type UpgradeStatus =
    | { state: "loading" }
    | { state: "error" }
    | { state: "unknown" }
    | { state: "available"; toVersion?: string; packageCount: number }
    | { state: "current"; version?: string };

/**
 * Reads a device's own package report the same way its Upgrade tab does: `os-*` entries with an
 * `upgradableTo` are the "Core Packages" a user can act on there. Other apt packages are super-admin
 * only and behind a toggle, so counting them would offer an upgrade most users could not find.
 */
const upgradeStatusFromReport = (report: VersionReport | undefined, device: Device): UpgradeStatus => {
    // A device that has never reported comes back as no report rather than an error, and
    // "never checked in" is not the same answer as "nothing pending".
    const packages = report?.packageVersions ?? [];
    if (packages.length === 0) return { state: "unknown" };

    const upgradable = packages.filter((p) => !!p.upgradableTo && p.name?.startsWith("os-"));
    if (upgradable.length === 0) return { state: "current", version: device.firmwareVersion };

    return {
        state: "available",
        toVersion: upgradable.find((p) => p.name === FIRMWARE_PACKAGE_NAME)?.upgradableTo,
        packageCount: upgradable.length,
    };
};

/**
 * Upgrade row action. Always present, so "already current" is distinguishable from "the menu has no
 * Upgrade entry" — it used to be hidden outright, which read as a missing feature and gave no way to
 * tell "up to date" from "we never managed to check".
 */
const UpgradeMenuItem: React.FC<{ status: UpgradeStatus; onClick: () => void }> = ({ status, onClick }) => {
    if (status.state === "available") {
        // Name the target when the firmware itself moves. When only other packages are pending (a
        // sensor driver, a model bundle) there is no single version to name, so say how many instead
        // of leaving a bare "Upgrade" that gives no idea of what it would do.
        const detail = status.toVersion ?? `${status.packageCount} package${status.packageCount === 1 ? "" : "s"}`;
        return <IconLink Icon={Icons.Upgrade} onClick={onClick}>{`Upgrade (${detail})`}</IconLink>;
    }

    const hoverText =
        status.state === "loading" ? "Checking for available upgrades..."
            : status.state === "error" ? "Could not check for available upgrades. Reload to try again."
                : status.state === "unknown" ? "This device has not reported its installed packages yet. You can still queue an upgrade for when it next checks in."
                    : `Device reported it is on the latest firmware (${status.version ?? "unknown version"}). You can still queue an upgrade in case a newer build has come out since.`;

    // A native title, not HoverTooltip: that one positions with popper against a ref read during
    // render, and inside DropDownMenu's portal the reference is still unresolved when the tooltip
    // shows, so it lands at the viewport origin — a caption in the far corner of the screen from the
    // menu you are pointing at. The browser puts a title at the cursor with no portal involved.
    return (
        // Not an IconLink: that one hardcodes link colors. Same layout, muted — but still clickable once
        // loaded, because the Upgrade tab can queue an install even when the device's last report
        // (or lack of one) shows nothing newer, e.g. a unit that was offline when a build came out.
        <div
            className={`inline-flex h-full text-muted-body-text ${status.state === "loading" ? "cursor-default" : "cursor-pointer"}`}
            title={hoverText}
            onClick={status.state === "loading" ? undefined : onClick}
        >
            <div className="self-center text-center align-middle pr-2">
                {status.state === "loading" ? <Spinner /> : <Icons.Upgrade className="my-auto" />}
            </div>
            <div className="self-center">Upgrade</div>
        </div>
    );
};

export const DevicesTable: React.FC<DevicesTableProps> = ({ devices, isLoading, onChange, lastTargets, facilityId }) => {
    const navigate = useNavigate();
    // Gates the alert-driven additions to this table, matching the bell / Alerts page / Health tab.
    const alertsVisible = useAlertsUiVisible();
    const deviceDialog = useEditModelDialog(Device);
    const confirmDialog = useConfirmDialog();
    const sendCommandDialog = useDialog<Device>();
    const changeLocationDialog = useChangeLocationDialog();
    const [commandToSend, setCommandToSend] = useState<DeviceCommandTypes>();
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [setupDevice, setSetupDevice] = useState<Device>();
    const commandToSendRef = useRef(commandToSend);
    const [upgradeStatuses, setUpgradeStatuses] = useState<Record<string, UpgradeStatus>>({});
    const [batchMode, setBatchMode] = useState(false);
    const [batchSelected, setBatchSelected] = useState<Record<string, boolean>>({});
    const batchCommandDialog = useDialog<Device[]>();
    const batchUpgradeDialog = useDialog<Device[]>();
    // Same ref pattern as commandToSendRef: the dialog OK handler is captured at show() time, so
    // it reads the package picker's latest selection through a ref instead of stale state.
    const batchUpgradePackagesRef = useRef<string[]>([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const user = useAuth();
    const isSuperAdmin = user.userClaims?.find((uc) => uc.claims.find((c) => c === CustomClaimValue.SuperAdmin));
    // Per device, mirroring the server (DeviceController / DeviceCommandController / VersionController):
    // ManageSettings (Administrator/Manager/Technician) to edit, run setup or send commands; EditDetails
    // (Administrator/Manager) to upgrade or move a device.
    const canManage = (d: Device | undefined) => hasClaim(user.userClaims, CustomClaimValue.ManageSettings, d?.organizationId, d?.facilityId);
    const canEditDetails = (d: Device | undefined) => hasClaim(user.userClaims, CustomClaimValue.EditDetails, d?.organizationId, d?.facilityId);
    // Whether the row menu would have anything in it. Every item needs at least one of these (the
    // super-admin-only ones included, since hasClaim passes super admins), so a plain User gets no
    // menu rather than one that opens empty — and no actions column at all if that holds for every row.
    const hasRowActions = (d: Device | undefined) => canManage(d) || canEditDetails(d);
    const showActionsColumn = (devices ?? []).some(hasRowActions);

    //Keep a ref to the commandToSend for our callback
    useEffect(() => {
        commandToSendRef.current = commandToSend;
    }, [commandToSend]);

    const handleAddOnClick: MouseEventHandler = () => {
        setShowAddDialog(true);
    };

    /**
     * Load a device's upgrade state when its row menu is opened.
     *
     * Per menu open rather than per page — or for every device at once — because the Upgrade item is
     * only ever SEEN while a menu is open, so anything broader queries for rows nobody looked at.
     * That matters here: devices insert a new version report on every check-in and nothing prunes
     * them, so version_reports and package_versions only grow. GetLatest is a single indexed lookup
     * by device id; asking "latest per device" across the whole table is an anti-join over all of
     * that history.
     *
     * Cached per device for the life of the mount: the answer only changes when the device reports
     * again, and reopening a menu should not re-query. A page refresh clears it.
     */
    const loadUpgradeStatus = async (device: Device) => {
        const id = device.id;
        if (!id || upgradeStatuses[id]) return;

        setUpgradeStatuses((prev) => ({ ...prev, [id]: { state: "loading" } }));
        try {
            const report = await Versions.latestByDeviceId(id);
            setUpgradeStatuses((prev) => ({ ...prev, [id]: upgradeStatusFromReport(report, device) }));
        } catch {
            setUpgradeStatuses((prev) => ({ ...prev, [id]: { state: "error" } }));
        }
    };

    /**
     * Edit a device from its row menu.
     *
     * Loads the device by id first rather than handing the dialog the row's own model: the list
     * endpoints (`for-organization`, `by-facilityid`) don't Include SensorConfig, only the by-id
     * fetch does. Without it the dialog's Radar section renders blank, and — because the save is a
     * whole-object PUT — submitting would post a device with no sensor config at all.
     */
    const handleEditOnClick = async (device: Device) => {
        let toEdit: Device;
        try {
            toEdit = (await Devices.getById(device.id)) ?? device;
        } catch (e) {
            toastError("Unable to load device", e + "");
            return;
        }
        deviceDialog.show(
            async (device) => {
                await Devices.save(device as Device);
                if (onChange) await onChange();
            },
            "Edit Device",
            toEdit,
        );
    };

    const handleUpgradeOnClick = (device: Device) => {
        navigate(Pages.DeviceDetails.getUrlWithQuery({ id: device.id! }, { tab: DeviceTab.Upgrade }));
    };
    const handleDeleteOnClick = async (device: Device) => {
        confirmDialog.show("Delete Device?", `Are you sure you want to delete this device [${device.name}]?`, "Delete", async () => {
            await Devices.deleteDevice(device.id);
            if (onChange) await onChange();
        });
    };
    const handleDisassociateOnClick = async (device: Device) => {
        // Use the device's own organization, not the user's currently-selected org.
        // The backend validates that the device belongs to the org id passed in, so a
        // mismatch both shows the wrong name and makes the removal fail server-side.
        if (!device.organizationId) {
            toastError("Cannot remove device", `[${device.name}] is not associated with an organization.`);
            return;
        }
        const org = await Organizations.getById(device.organizationId);
        confirmDialog.show("Remove Device?", `Are you sure you want to remove [${device.name}] from ${org?.name}?`, "Remove from Organization", async () => {
            await Organizations.removeDevice(org?.id, device.id);
            if (onChange) await onChange();
        });
    };
    const handlePurgeAggregations = async (device: Device) => {
        confirmDialog.show("Purge Report Data?", `Are you sure purge data for [${device.name}] ? It may take up to an hour to recalculate (typically 5-10 minutes)`, "Purge Report Data", async () => {
            try {
                await Devices.purgeAggregations(device.id);
                toastSuccess("Data Purged", `Report data for  [${device.name}] was purged successfully`);
            } catch (e) {
                toastError("Unable to purge data", e + "");
            }
        });
    };
    // Selection survives filtering/paging on purpose: a device checked and then filtered out of
    // view is still acted on, and the "N selected" count in the toolbar always shows the truth.
    const batchDevices = useMemo(() => (devices ?? []).filter((d) => d.id && batchSelected[d.id!]), [devices, batchSelected]);

    const exitBatchMode = () => {
        setBatchMode(false);
        setBatchSelected({});
    };

    const toggleBatchDevice = (id: string | undefined) => {
        if (!id) return;
        setBatchSelected((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    /**
     * Run one request per selected device, individually error-tolerant, then summarize in toasts.
     * Always exits batch mode afterwards — the batch was issued, even if some devices refused
     * (e.g. pending commands); the failure toast names the stragglers to retry.
     */
    const runBatch = async (targets: Device[], actionLabel: string, action: (device: Device) => Promise<unknown>) => {
        const failed: Device[] = [];
        const reasons = new Set<string>();
        await Promise.all(targets.map(async (device) => {
            try {
                await action(device);
            } catch (e) {
                failed.push(device);
                if (e instanceof Error && e.message) reasons.add(e.message);
            }
        }));
        const okCount = targets.length - failed.length;
        if (okCount > 0) {
            toastSuccess(`${actionLabel} Sent`, `${actionLabel} sent to ${okCount} device${okCount === 1 ? "" : "s"}.`);
        }
        if (failed.length > 0) {
            toastError(`${actionLabel} failed for ${failed.length} device${failed.length === 1 ? "" : "s"}`, <>
                <div>{failed.map((d) => d.name).join(", ")}</div>
                {[...reasons].map((reason) => <div key={reason}>{reason}</div>)}
            </>);
        }
        exitBatchMode();
        if (onChange) await onChange();
    };

    const handleBatchRebootClick = () => {
        const targets = batchDevices;
        confirmDialog.show("Reboot Devices?", `Are you sure you want to reboot the ${targets.length} selected device${targets.length === 1 ? "" : "s"}?`, "Reboot", async () => {
            await runBatch(targets, "Reboot Command", (device) => DeviceCommands.sendCommand(new RebootCommand({ deviceId: device.id } as IRebootCommand)));
        });
    };

    const handleBatchSendCommandClick = () => {
        const targets = batchDevices;
        setCommandToSend(undefined);
        batchCommandDialog.show("Send Device Command", "Send", targets, async () => {
            const command = commandToSendRef.current;
            if (!command) throw new UserError(["Select a command to send."]);
            // Re-stamp the composed command with each target's id: the editor builds one command,
            // the server wants one row per device. Spread keeps $type, so the server still
            // deserializes the right command subtype.
            await runBatch(targets, "Command", (device) => DeviceCommands.sendCommand({ ...command, deviceId: device.id } as DeviceCommand));
        });
    };

    const handleBatchUpgradeClick = () => {
        const targets = batchDevices;
        batchUpgradePackagesRef.current = [];
        batchUpgradeDialog.show("Upgrade Devices", "Upgrade", targets, async () => {
            const packages = batchUpgradePackagesRef.current;
            if (!packages.length) throw new UserError(["Select at least one package to upgrade."]);
            await runBatch(targets, "Upgrade Request", (device) => Versions.updateDevice(device.id, packages));
        });
    };

    // The batch command editor only offers firmware-gated commands (Update Photo) when every
    // selected device supports them, so gate on the lowest firmware in the selection.
    // compareVersions ranks undefined as 0.0.0, which correctly treats an unknown version as
    // "assume it can't".
    const batchMinFirmware = batchDevices.length
        ? batchDevices.map((d) => d.firmwareVersion).reduce((a, b) => (compareVersions(a, b) <= 0 ? a : b))
        : undefined;

    const handleOnRowClick = (device: Device) => {
        if (batchMode) {
            toggleBatchDevice(device.id);
            return;
        }
        navigate(Pages.DeviceDetails.getUrl({ id: device.id! }));
    };
    const handleSendCommandClick = (device: Device) => {
        setCommandToSend(undefined);
        sendCommandDialog.show("Send Device Command", "Send", device, async () => {
            try {
                await DeviceCommands.sendCommand(commandToSendRef.current as DeviceCommand);
                toastSuccess("Command Sent", "A new command is pending for " + device.name);
                if (onChange) await onChange();
            } catch (e) {
                toastError("Unable to send command", e instanceof Error ? e.message : String(e));
            }
        });
    };

    const handleChangeLocationClick = (device: Device) => {
        changeLocationDialog.show(device, async (oldF, newF) => {
            try {
                await Devices.changeFacility(device.id, newF?.id ?? "00000000-0000-0000-0000-000000000000");
                toastSuccess(
                    "Success",
                    <div>
                        <div>Location changed for {device.name}</div>
                        <div>{(oldF?.name ?? "None") + ` --> ` + (newF?.name ?? "None")}</div>
                    </div>,
                    10000,
                );
                if (onChange) await onChange();
            } catch (e) {
                toastError("Unable to change location", e + "");
            }
        });
    };
    const statusConvert = (deviceStatus: DeviceStatus | undefined) => {
        if (deviceStatus === undefined || deviceStatus === DeviceStatus.BadConfig || deviceStatus === DeviceStatus.BadData || deviceStatus === DeviceStatus.Unknown || deviceStatus === DeviceStatus.Degraded) {
            return "warn";
        }
        // Transient and self-announced: the unit said it is restarting to apply settings. Amber,
        // not red — without this it fell through to "error" and every settings push painted the
        // fleet with failure dots for the length of a pipeline restart.
        if (deviceStatus === DeviceStatus.Reconfiguring) {
            return "warn";
        }
        if (deviceStatus === DeviceStatus.Healthy) {
            return "ok";
        }
        if (deviceStatus === DeviceStatus.MissingPackage) {
            return "alert";
        }
        return "error";
    };

    // Recently-changed window: 6 hours. Long enough to draw the eye on a
    // device that went bad overnight, short enough that healthy devices don't
    // wear the badge indefinitely.
    const isRecentlyChanged = (d: Device | undefined) => {
        if (!d?.lastStatusChange) return false;
        const since = Date.now() - new Date(d.lastStatusChange).getTime();
        return since >= 0 && since < 6 * 60 * 60 * 1000;
    };

    // Sorting on the raw enum is close to useless: the values run Healthy=1, RadarDown=2,
    // BadConfig=4 … LostConnection=999, so "sort by status" would interleave a dead device with a
    // misconfigured one by numeric accident. This ranks by how much attention the device needs, so
    // one click puts everything that's wrong at the top — which is the only reason to sort this
    // column at all.
    const statusRank = (s: DeviceStatus | undefined): number => {
        switch (s) {
            case DeviceStatus.LostConnection: return 0;
            case DeviceStatus.RadarDown: return 1;
            case DeviceStatus.BadData: return 2;
            case DeviceStatus.BadConfig: return 3;
            case DeviceStatus.Degraded: return 4;
            case DeviceStatus.PartialConnection: return 5;
            // Worth a glance (its sensors are down mid-restart) but transient and self-resolving,
            // so it sits below the genuine faults and above the merely-unpackaged.
            case DeviceStatus.Reconfiguring: return 6;
            case DeviceStatus.MissingPackage: return 7;
            case DeviceStatus.Unknown: return 8;
            case DeviceStatus.Healthy: return 9;
            default: return 10;   // undefined — nothing reported yet
        }
    };

    const columns: SortableTableHeaderType<TableDataType>[] = [
        {
            header: "Status",
            dataKey: "status",
            sortFunc: (a, b) => statusRank(a?.status) - statusRank(b?.status),
            renderFunc: (d) => (
                <span className="inline-flex items-center gap-1">
                    <StatusDot status={statusConvert(d?.status)} hoverMessage={getStatusHoverText(d)} />
                    {/* Superadmin-only, like the rest of the alerting UI. The badge is driven by alert
                        activity, so showing it to customers would advertise a feature they can't see,
                        configure or act on — and draw the eye to devices they can't do anything about. */}
                    {alertsVisible && isRecentlyChanged(d) && (
                        <span
                            title="Status changed in the last 6 hours"
                            className="text-[10px] font-bold uppercase bg-blue-500 text-white px-1 rounded"
                        >
                            New
                        </span>
                    )}
                </span>
            ),
            width: 90,
            cardPosition: CardPosition.TopRight,
        },
        {
            header: "Name",
            dataKey: "name",
            renderFunc: (d) => {
                return !isSuperAdmin ? (
                    d?.name
                ) : (
                    <HoverTooltip hoverable={d?.name} delayMs={1500}>
                        {d?.id}
                    </HoverTooltip>
                );
            },
            width: 200,
        },
        {
            header: "Actions",
            width: 100,
            cardPosition: CardPosition.BottomRight,
            renderFunc: (device) => (
                <button
                    className="px-3 py-1 text-xs font-medium rounded-md transition-colors duration-150 bg-orange-400 hover:bg-orange-500 text-white shadow-sm"
                    onClick={(e) => {
                        e.stopPropagation();
                        window.location.href = `${Pages.DeviceDetails.getUrlWithQuery({ id: device?.id }, { tab: DeviceTab.Camera })}`;
                    }}
                >
                    View Camera
                </button>
            ),
        },
        {
            header: "Location",
            renderFunc: (d) => d?.facilityName ?? "Unassigned",
            dataKey: "facilityName",
            width: 180,
        },
        {
            header: "IP Address",
            dataKey: "ipv4Address",
            width: 140,
        },
        {
            header: "Version",
            dataKey: "firmwareVersion",
            width: 100,
            sortFunc: (a, b) => compareVersions(a.firmwareVersion, b.firmwareVersion),
            renderFunc: (d) => <div className="flex flex-row items-center gap-x-2">{d?.firmwareVersion}</div>,
        },
        ...(showActionsColumn ? [{
            header: "",
            width: 20,
            renderFunc: (device: TableDataType | undefined) =>
                isLoading || device?.hasPendingDeviceCommands ? (
                    <Spinner />
                ) : !hasRowActions(device) ? null : (
                    <DropDownMenu onShow={() => loadUpgradeStatus(device!)}>
                        {/* Same gate as the Upgrade tab on DeviceDetails, or this links to a tab the user can't see. */}
                        {canEditDetails(device) && (
                            <UpgradeMenuItem status={upgradeStatuses[device?.id ?? ""] ?? { state: "loading" }} onClick={() => handleUpgradeOnClick(device!)} />
                        )}
                        {canManage(device) && (<>
                            <IconLink Icon={Icons.Edit} onClick={() => handleEditOnClick(device!)}>
                                Edit
                            </IconLink>
                            {facilityId && (
                                <IconLink Icon={Icons.Settings} onClick={() => setSetupDevice(device!)}>
                                    Run Setup
                                </IconLink>
                            )}
                            <IconLink Icon={Icons.Send} onClick={() => handleSendCommandClick(device!)}>
                                Send Command
                            </IconLink>
                        </>)}
                        {canEditDetails(device) && (
                            <IconLink Icon={Icons.NavTruck} onClick={() => handleChangeLocationClick(device!)}>
                                Change Location
                            </IconLink>
                        )}
                        {/* Org-level only, matching DeviceController.PurgeAggregations. */}
                        {hasClaim(user.userClaims, CustomClaimValue.EditDetails, device?.organizationId) && (
                            <IconLink Icon={Icons.Refresh} onClick={() => handlePurgeAggregations(device!)}>
                                Recalculate Reports
                            </IconLink>
                        )}
                        {/* Super admin only, matching the server: both pull the device out of the customer's hands. */}
                        {isSuperAdmin && (<>
                            <div className="border-t text-brand-primary  !mt-6 !mb-4" />
                            <IconLink Icon={Icons.Hide} onClick={() => handleDisassociateOnClick(device!)}>
                                Disassociate
                            </IconLink>
                            <IconLink Icon={Icons.Trash} onClick={() => handleDeleteOnClick(device!)}>
                                Delete
                            </IconLink>
                        </>)}
                    </DropDownMenu>
                ),
        }] : []),
    ];

    if (lastTargets?.length) {
        columns.splice(3, 0, {
            header: "Last Target",
            dataKey: "lastUpdate",
            renderFunc: (d) => {
                const lastUpdate = lastTargets.find((lt) => lt.deviceId === d?.id!)?.lastUpdate;
                //const locale = navigator.language || navigator.languages[0];
                const zone = Intl.DateTimeFormat(undefined, { timeZoneName: "short" }).resolvedOptions().timeZone;
                if (lastUpdate) {
                    return (
                        <HoverTooltip placement="right" hoverable={format(lastUpdate, "MMM dd HH:mm", { timeZone: zone })}>
                            <span>{lastUpdate.toLocaleDateString() + " " + lastUpdate.toLocaleTimeString()}</span>
                        </HoverTooltip>
                    );
                }
                return "Unknown";
            },
            width: 180,
        });
    }

    // Unshifted after the Last Target splice above so that splice's index math is untouched.
    if (batchMode) {
        columns.unshift({
            header: "",
            width: 36,
            renderFunc: (device) => (
                <input
                    type="checkbox"
                    aria-label={`Select ${device?.name ?? "device"}`}
                    checked={!!batchSelected[device?.id ?? ""]}
                    onChange={() => toggleBatchDevice(device?.id)}
                    // Without this, the click also reaches the row handler, which toggles the
                    // same device right back.
                    onClick={(e) => e.stopPropagation()}
                />
            ),
        });
    }

    const devicesWithLastTarget = useMemo(() => {
        return devices?.map((d) => ({ ...d, lastUpdate: lastTargets?.find((lt) => lt.deviceId === d.id)?.lastUpdate }));
    }, [devices, lastTargets]);

    const queryFromUrl = searchParams.get("q") ?? "";
    const statusFromUrl = searchParams.get("status") ?? "";
    const sortKeyFromUrl = (searchParams.get("sort") ?? DEFAULT_SORT_KEY) as string & keyof TableDataType;
    const sortDirFromUrl: "asc" | "desc" = searchParams.get("sortDir") === "desc" ? "desc" : "asc";
    const pageFromUrl = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);
    const pageSizeFromUrl = (() => {
        const raw = parseInt(searchParams.get("pageSize") ?? "", 10);
        return PAGE_SIZE_OPTIONS.includes(raw) ? raw : DEFAULT_PAGE_SIZE;
    })();

    const applyStatusFilter = (rows: typeof devicesWithLastTarget) => {
        if (!statusFromUrl || !rows) return rows;
        if (statusFromUrl === "unhealthy") {
            return rows.filter(d => d.status !== undefined && d.status !== DeviceStatus.Healthy);
        }
        const wanted = Number(statusFromUrl);
        return rows.filter(d => d.status === wanted);
    };

    const filteredData = useMemo(() => {
        const query = queryFromUrl.trim().toLowerCase();
        if (!query.length) {
            return applyStatusFilter(devicesWithLastTarget);
        }
        return applyStatusFilter(
            devicesWithLastTarget?.filter((d) => {
                const haystack = [d.name, d.deviceKey, d.facilityName, d.description, d.ipv4Address, d.firmwareVersion, d.id, d.notes?.map((n) => n.note).join(" ")].filter(Boolean).join(" ").toLowerCase();
                return haystack.includes(query);
            }) ?? []
        );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryFromUrl, statusFromUrl, devicesWithLastTarget]);

    const handleSearch = (terms: string | undefined | null) => {
        setSearchParams(
            (existing) => {
                if (terms) {
                    existing.set("q", terms);
                } else {
                    existing.delete("q");
                }
                existing.delete("page");
                return existing;
            },
            { replace: true, preventScrollReset: true },
        );
    };

    const handleStatusFilterChange = (value: string) => {
        setSearchParams(existing => {
            if (value) existing.set("status", value);
            else existing.delete("status");
            existing.delete("page");
            return existing;
        }, { replace: true, preventScrollReset: true });
    }

    const handleSortChange = (sortKey: string, direction: "asc" | "desc") => {
        setSearchParams(
            (existing) => {
                if (sortKey === DEFAULT_SORT_KEY && direction === "asc") {
                    existing.delete("sort");
                    existing.delete("sortDir");
                } else {
                    existing.set("sort", sortKey);
                    existing.set("sortDir", direction);
                }
                return existing;
            },
            { replace: true, preventScrollReset: true },
        );
    };

    const handlePageChange = (page: number) => {
        setSearchParams(
            (existing) => {
                if (page <= 1) existing.delete("page");
                else existing.set("page", page.toString());
                return existing;
            },
            { replace: true, preventScrollReset: true },
        );
    };

    const handlePageSizeChange = (size: number) => {
        if (!PAGE_SIZE_OPTIONS.includes(size)) return;
        setSearchParams(
            (existing) => {
                if (size === DEFAULT_PAGE_SIZE) existing.delete("pageSize");
                else existing.set("pageSize", size.toString());
                existing.delete("page");
                return existing;
            },
            { replace: true, preventScrollReset: true },
        );
    };
    return (
        <>
            <div className="flex flex-wrap justify-end m-5 gap-2 items-center">
                {batchMode ? (
                    <div className="mr-auto flex flex-wrap gap-2 items-center">
                        <span className="text-sm font-medium whitespace-nowrap">
                            {batchDevices.length} selected
                        </span>
                        {/* Disabled rather than trimmed when the selection includes a device the user can't
                            act on, so nothing is silently skipped; the server would refuse those anyway. */}
                        <Button variant={ButtonVariants.Destructive} disabled={!batchDevices.length || !batchDevices.every(canManage)} onClick={handleBatchRebootClick}>
                            Reboot
                        </Button>
                        <Button variant={ButtonVariants.Secondary} disabled={!batchDevices.length || !batchDevices.every(canManage)} onClick={handleBatchSendCommandClick}>
                            Send Command
                        </Button>
                        {(devices ?? []).some(canEditDetails) && (
                            <Button variant={ButtonVariants.Secondary} disabled={!batchDevices.length || !batchDevices.every(canEditDetails)} onClick={handleBatchUpgradeClick}>
                                Upgrade
                            </Button>
                        )}
                        <Button onClick={exitBatchMode}>Done</Button>
                    </div>
                ) : (devices ?? []).some(canManage) ? (
                    <Button className="mr-auto" variant={ButtonVariants.Secondary} onClick={() => setBatchMode(true)}>
                        Batch Select
                    </Button>
                ) : null}
                <select
                    value={statusFromUrl}
                    onChange={(e) => handleStatusFilterChange(e.target.value)}
                    className="bg-background-input rounded p-1 text-sm"
                    aria-label="Filter by device status"
                >
                    <option value="">All statuses</option>
                    <option value="unhealthy">Anything not Healthy</option>
                    <option value={String(DeviceStatus.Healthy)}>Healthy</option>
                    <option value={String(DeviceStatus.Degraded)}>Degraded</option>
                    <option value={String(DeviceStatus.RadarDown)}>Radar Down</option>
                    <option value={String(DeviceStatus.LostConnection)}>Lost Connection</option>
                    <option value={String(DeviceStatus.BadConfig)}>Bad Configuration</option>
                </select>
                <SearchInput onSearch={handleSearch} initialSearch={queryFromUrl} />
            </div>
            {/* `key` is load-bearing: SortableTable holds its own internal page/sort state in client-side
                mode, and the onChange callbacks only feed the URL. Remounting on query change snaps the
                internal state back to the URL-derived `initialPage`/`initialSortKey`/`initialSortDir`,
                keeping the two in sync (e.g. when a search resets pagination). Don't drop this `key`. */}
            <SortableTable
                key={`devices-${queryFromUrl}`}
                isLoading={isLoading}
                onRowClick={handleOnRowClick}
                initialSortKey={sortKeyFromUrl}
                initialSortDir={sortDirFromUrl}
                initialPage={pageFromUrl}
                columns={columns}
                data={filteredData}
                emptyValue={<div className="my-5">No devices have been added to this location.</div>}
                pagination
                defaultPageSize={pageSizeFromUrl}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                onSortChange={handleSortChange}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
            />

            {facilityId && (
                <div className="w-full flex flex-col justify-center items-center my-5">
                    <Button onClick={handleAddOnClick}>Add Device</Button>
                </div>
            )}

            {queryFromUrl && (
                <div className="m-3 flex justify-center font-semibold text-brand-primary">
                    <h3 className="mx-auto">{`${filteredData?.length} results from query: '${queryFromUrl}'`}</h3>
                </div>
            )}

            {facilityId && (
                <DeviceProvisionDialog
                    facilityId={facilityId}
                    existingDevice={setupDevice}
                    show={showAddDialog || !!setupDevice}
                    onCancel={() => {
                        setShowAddDialog(false);
                        setSetupDevice(undefined);
                        if (onChange) onChange();
                    }}
                    onContinueToLayout={(deviceId) => navigate(`${Pages.FacilityDetails.getUrl({ id: facilityId })}?tab=${FacilityTab.Layout}&alignDevice=${deviceId}`)}
                />
            )}
            {deviceDialog.renderModal(
                (model, helpers, onSubmit, onCancel) => (
                    <EditDeviceDetails onSubmit={onSubmit} onCancel={onCancel} device={model} helpers={helpers} />
                ),
                "w-full max-w-5xl rounded-2xl",
            )}
            {sendCommandDialog.renderDialog(
                (device) => (
                    <EditDeviceCommand command={commandToSend} firmwareVersion={device?.firmwareVersion} deviceId={device?.id ?? ""} onChange={(command) => setCommandToSend(command)} />
                ),
                { closeOnOutsideClick: false },
            )}
            {batchCommandDialog.renderDialog(
                (targets) => (
                    <>
                        <div className="mb-3 text-sm text-muted-body-text">
                            This command will be sent to {targets?.length ?? 0} selected device{(targets?.length ?? 0) === 1 ? "" : "s"}.
                        </div>
                        {/* deviceId is a placeholder — the composed command is re-stamped per device on send. */}
                        <EditDeviceCommand command={commandToSend} firmwareVersion={batchMinFirmware} deviceId={""} onChange={(command) => setCommandToSend(command)} />
                    </>
                ),
                { closeOnOutsideClick: false },
            )}
            {batchUpgradeDialog.renderDialog(
                (targets) => (
                    <BatchDeviceUpgradeView devices={targets ?? []} onSelectionChange={(packageNames) => { batchUpgradePackagesRef.current = packageNames; }} />
                ),
                { className: "w-full max-w-3xl", closeOnOutsideClick: false },
            )}
            {changeLocationDialog.renderDialog()}
            {confirmDialog.renderDialog()}
        </>
    );
};
